// theme-sources/js/theme/components/cart.js

import { logger } from "../core/logger";
import { THEME_EVENTS, publish, subscribe } from "../../shared/events";
import { trapFocus } from "../../shared/utils";
import { CART_CONFIG } from "../core/config";
import { fetchConfig, debounce } from "../core/utils";
import { syncCartFakeItem } from "../features/cart-fake-item";

async function logRealCart(label) {
  try {
    const cart = await fetch(`${theme.routes.cart_url}.js`, {
      headers: { Accept: "application/json" },
    }).then((response) => response.json());

    logger.info("REAL_CART", label, {
      item_count: cart.item_count,
      items: (cart.items || []).map((item, index) => ({
        line: index + 1,
        key: item.key,
        id: item.id,
        quantity: item.quantity,
        title: item.product_title,
        variant: item.variant_title,
        properties: item.properties,
      })),
    });
  } catch (error) {
    logger.error("REAL_CART", `log failed: ${label}`, error);
  }
}

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      event.preventDefault();

      const cartItems = this.closest("cart-drawer-items");
      if (!cartItems) return;

      logger.info("CartRemoveButton", "cart-items", cartItems);

      if (this.dataset.removePack === "true") {
        logger.info("CartRemoveButton", "removePack");
        cartItems.removePackFromCart();
        return;
      }

      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}
if (!customElements.get("cart-remove-button")) {
  customElements.define("cart-remove-button", CartRemoveButton);
}

class CartDrawerItems extends HTMLElement {
  constructor() {
    super();

    this.isConvertingSubscription = false;

    this.lineItemStatusElement =
      document.getElementById("CartDrawer-LineItemStatus") ||
      document.getElementById("shopping-cart-line-item-status");

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, CART_CONFIG.ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener("change", debouncedOnChange.bind(this));

    this.addEventListener("click", (event) => {
      const subscribeButton = event.target.closest(
        "#CartDrawer-SubscribeButton",
      );
      if (!subscribeButton) return;
      logger.info("CartDrawerItems", "subscribe button clicked", {
        rawData: subscribeButton.dataset.subscribableItems,
      });
      event.preventDefault();
      this.convertSubscribableItems(subscribeButton);
    });
    this.handlePageShow = this.handlePageShow.bind(this);
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    logger.info("CartDrawerItems", "connectedCallback");

    this.cartUpdateUnsubscriber = subscribe(
      THEME_EVENTS.cartUpdate,
      (event) => {
        logger.info("Cart", "cartUpdate", event.source);
        if (event.source === "cart-drawer-items") return;
        this.onCartUpdate();
      },
    );
    window.addEventListener("pageshow", this.handlePageShow);
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    window.removeEventListener("pageshow", this.handlePageShow);
  }

  get cartDrawer() {
    return document.querySelector("cart-drawer");
  }

  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: this.cartDrawer?.dataset.sectionId,
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }

  handlePageShow(event) {
    logger.info("CartDrawerItems", "pageshow", {
      persisted: event.persisted,
      href: window.location.href,
    });

    console.log("[CartDrawerItems] pageshow", {
      persisted: event.persisted,
      href: window.location.href,
    });

    if (!event.persisted) return;

    console.log("[CartDrawerItems] calling onCartUpdate()");
    this.onCartUpdate();
  }

  onCartUpdate() {
    logger.info("CartDrawerItems", "onCartUpdate");

    const cartDrawerWrapper = this.cartDrawer;
    cartDrawerWrapper?.classList.remove("is-empty");

    const drawerSectionId = cartDrawerWrapper?.dataset.sectionId;
    if (!drawerSectionId) {
      logger.warn("CartDrawerItems", "onCartUpdate missing drawerSectionId");
      return;
    }

    console.log("[CartDrawerItems] onCartUpdate", {
      drawerSectionId,
      hasCartDrawerWrapper: !!cartDrawerWrapper,
    });

    //fetch(`${theme.routes.cart_url}?section_id=${drawerSectionId}`)
    fetch(
      `${theme.routes.cart_url}?section_id=${drawerSectionId}&_ts=${Date.now()}`,
      {
        cache: "no-store",
      },
    )
      .then((response) => response.text())
      .then((responseText) => {
        console.log("[CartDrawerItems] onCartUpdate response", {
          length: responseText.length,
          hasDiscountCodeUI: responseText.includes("data-discount-code"),
        });
        const html = new DOMParser().parseFromString(responseText, "text/html");
        const selectors = ["cart-drawer-items", "cart-drawer-footer"];

        for (const selector of selectors) {
          const targetElement = document.querySelector(selector);
          const sourceElement = html.querySelector(selector);

          console.log("[CartDrawerItems] replace selector", {
            selector,
            targetFound: !!targetElement,
            sourceFound: !!sourceElement,
          });

          if (targetElement && sourceElement) {
            targetElement.replaceWith(sourceElement);
          }
        }
      })
      .catch((error) => {
        logger.error("CartDrawerItems", "onCartUpdate error", error);
      });
  }

  resetQuantityInput(id) {
    const input =
      this.querySelector(`#CartDrawer-Quantity-${id}`) ||
      this.querySelector(`#Quantity-${id}`);

    if (!input) return;

    input.value = input.getAttribute("value");
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    logger.info("CartDrawerItems", "validateQuantity", {
      input: event.target,
      inputIsActive: document.activeElement === event.target,
    });

    const inputValue = parseInt(event.target.value, 10);
    const index = event.target.dataset.index;
    let message = "";

    logger.info("CartDrawerItems", "inputValue", inputValue);

    if (inputValue < Number(event.target.dataset.min)) {
      message = window.quickOrderListStrings.min_error.replace(
        "[min]",
        event.target.dataset.min,
      );
    } else if (inputValue > parseInt(event.target.max, 10)) {
      message = window.quickOrderListStrings.max_error.replace(
        "[max]",
        event.target.max,
      );
    } else if (inputValue % parseInt(event.target.step, 10) !== 0) {
      message = window.quickOrderListStrings.step_error.replace(
        "[step]",
        event.target.step,
      );
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();

      this.updateQuantity(
        index,
        inputValue,
        document.activeElement?.getAttribute("name"),
        event.target.dataset.quantityVariantId,
      );
    }
  }

  onChange(event) {
    logger.info("CartDrawerItems", "onChange activeElement", {
      activeElement: document.activeElement,
      tag: document.activeElement?.tagName,
      name: document.activeElement?.getAttribute("name"),
    });

    logger.info("CartDrawerItems", "event target", {
      target: event.target,
      targetTag: event.target?.tagName,
      targetName: event.target?.getAttribute("name"),
    });

    this.validateQuantity(event);
  }

  removePackFromCart() {
    logger.info("CartDrawerItems", "removePackFromCart");

    const pack = this.querySelector(".cart-pack-item");
    if (!pack) return;

    const keys = (pack.dataset.packKeys || "")
      .split(",")
      .map((key) => key.trim())
      .filter(Boolean);

    if (!keys.length) return;

    pack.classList.add("is-updating");

    const btn = pack.querySelector("cart-remove-button");
    btn?.setAttribute("aria-disabled", "true");
    if (btn) btn.disabled = true;

    const updates = {};

    keys.forEach((key) => {
      const variantId = key.split(":")[0];
      if (variantId) updates[variantId] = 0;
    });

    const body = JSON.stringify({
      updates,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    logger.info("CartDrawerItems", "removePack keys", {
      raw: pack.dataset.packKeys,
      split: keys,
      updates,
    });

    fetch(`${theme.routes.cart_update_url}`, { ...fetchConfig(), body })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);

        logger.info("CartDrawerItems", "removePack parsedState", parsedState);

        if (parsedState.errors) {
          throw new Error(parsedState.errors);
        }

        return syncCartFakeItem({
          baseState: parsedState,
          sections: this.getSectionsToRender().map(
            (section) => section.section,
          ),
          sectionsUrl: window.location.pathname,
        });
      })
      .then(async (finalState) => {
        logger.info("CartDrawerItems", "removePack finalState", {
          hasSections: !!finalState?.sections,
          sectionKeys: Object.keys(finalState?.sections || {}),
        });

        this.renderDrawerState(finalState);

        await logRealCart("after updateQuantity");
      })
      .catch((error) => {
        logger.error("CartDrawerItems", "removePackFromCart error", error);
      })
      .finally(() => {
        pack.classList.remove("is-updating");
        btn?.setAttribute("aria-disabled", "false");
        if (btn) btn.disabled = false;
      });
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    logger.info("CartDrawerItems", "SECTIONS", body);

    fetch(`${theme.routes.cart_change_url}`, { ...fetchConfig(), body })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);

        logger.info("CartDrawerItems", "parsedState change", parsedState);

        if (parsedState.errors) {
          return this.handleErrorState(parsedState, line);
        }

        return syncCartFakeItem({
          baseState: parsedState,
          sections: this.getSectionsToRender().map(
            (section) => section.section,
          ),
          sectionsUrl: window.location.pathname,
        }).then(async (finalState) => {
          logger.info("CartDrawerItems", "finalState after fake sync", {
            hasSections: !!finalState?.sections,
            sectionKeys: Object.keys(finalState?.sections || {}),
          });

          this.renderDrawerState(finalState, { line, name, variantId });
          await logRealCart("after removePackFromCart");
        });
      })
      .catch((error) => {
        logger.error("CartDrawerItems", "Error", error);

        const errors = document.getElementById("CartDrawer-CartErrors");
        if (errors) {
          errors.textContent = window.cartStrings.error;
        }
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  handleErrorState(parsedState, line) {
    const quantityElement = document.getElementById(
      `CartDrawer-Quantity-${line}`,
    );
    if (quantityElement) {
      quantityElement.value = quantityElement.getAttribute("value");
    }

    this.updateLiveRegions(line, parsedState.errors);
  }

  renderDrawerState(parsedState, { line, name, variantId } = {}) {
    logger.info("CartDrawerItems", "renderDrawerState", {
      line,
      name,
      variantId,
      parsedState,
    });

    const cartDrawer = this.cartDrawer;
    const quantityElement =
      line != null
        ? document.getElementById(`CartDrawer-Quantity-${line}`)
        : null;

    if (cartDrawer) {
      cartDrawer.classList.toggle("is-empty", parsedState.item_count === 0);

      const drawerSectionId = cartDrawer.dataset.sectionId;
      const hasRenderableSections =
        !!parsedState?.sections &&
        !!drawerSectionId &&
        !!parsedState.sections[drawerSectionId] &&
        !!parsedState.sections["cart-icon-bubble"];

      logger.info("CartDrawerItems", "before renderContents", {
        hasSections: !!parsedState?.sections,
        sectionKeys: Object.keys(parsedState?.sections || {}),
        hasRenderableSections,
        parsedState,
      });

      if (hasRenderableSections) {
        cartDrawer.renderContents(parsedState);
      }
    }

    let message = "";
    const updatedValue =
      line != null && parsedState.items?.[line - 1]
        ? parsedState.items[line - 1].quantity
        : undefined;

    if (quantityElement && typeof updatedValue !== "undefined") {
      if (updatedValue !== parseInt(quantityElement.value, 10)) {
        message = window.cartStrings.quantityError.replace(
          "[quantity]",
          updatedValue,
        );
      }
    }

    this.updateLiveRegions(line, message);

    const lineItem =
      line != null ? document.getElementById(`CartDrawer-Item-${line}`) : null;

    const targetInput =
      lineItem && name ? lineItem.querySelector(`[name="${name}"]`) : null;

    if (targetInput && cartDrawer) {
      trapFocus(cartDrawer, targetInput);
    } else if (parsedState.item_count === 0 && cartDrawer) {
      const emptyState = cartDrawer.querySelector(".drawer__inner-empty");
      const link = cartDrawer.querySelector("a");

      if (emptyState && link) {
        trapFocus(emptyState, link);
      }
    } else if (cartDrawer) {
      const firstItem = cartDrawer.querySelector(".cart-item__name");
      if (firstItem) {
        trapFocus(cartDrawer, firstItem);
      }
    }

    logRealCart("after renderDrawerState");

    this.syncLandingProductForms(parsedState);

    publish(THEME_EVENTS.cartUpdate, {
      source: "cart-drawer-items",
      cartData: parsedState,
      variantId,
    });
  }

  updateLiveRegions(line, message) {
    const lineItemError = document.getElementById(
      `CartDrawer-LineItemError-${line}`,
    );
    if (lineItemError) {
      const errorText = lineItemError.querySelector(".cart-item__error-text");
      if (errorText) {
        errorText.innerHTML = message;
      }
    }

    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute("aria-hidden", true);
    }

    const cartStatus = document.getElementById("CartDrawer-LiveRegionText");
    if (!cartStatus) return;

    cartStatus.setAttribute("aria-hidden", false);

    setTimeout(() => {
      cartStatus.setAttribute("aria-hidden", true);
    }, 1000);
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById("CartDrawer-CartItems");
    if (mainCartItems) {
      mainCartItems.classList.add("cart__items--disabled");
    }

    document.documentElement.classList.add("cart-is-updating");

    logger.info("CartDrawerItems", "mainCartItems", mainCartItems);

    const items = this.querySelectorAll(`#CartDrawer-Item-${line}`);

    items.forEach((item) => {
      item.classList.add("is-updating");

      if (!item.querySelector(".cart-item__loader")) {
        const loader = document.createElement("div");
        loader.className = "cart-item__loader";
        loader.innerHTML = `<div class="cart-spinner"></div>`;
        item.appendChild(loader);
      }
    });
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById("CartDrawer-CartItems");
    if (mainCartItems) {
      mainCartItems.classList.remove("cart__items--disabled");
    }

    document.documentElement.classList.remove("cart-is-updating");

    const items = this.querySelectorAll(`#CartDrawer-Item-${line}`);

    items.forEach((item) => {
      item.classList.remove("is-updating");
      const loader = item.querySelector(".cart-item__loader");
      if (loader) loader.remove();
    });
  }

  parseSubscribableItems(rawData) {
    logger.info("CartDrawerItems", "parseSubscribableItems rawData", {
      rawData,
    });

    if (!rawData) return [];

    const items = rawData
      .split("||")
      .map((entry) => {
        const [key, variantId, sellingPlanId, quantity, sellingPlansRaw] =
          entry.split("::");

        const sellingPlans = (sellingPlansRaw || "")
          .split("##")
          .map((planEntry) => {
            const [name, id] = planEntry.split("==");

            return {
              name,
              id: Number(id),
            };
          })
          .filter((plan) => plan.name && Number.isFinite(plan.id));

        return {
          key,
          variantId: Number(variantId),
          sellingPlanId: Number(sellingPlanId),
          quantity: Number(quantity),
          sellingPlans,
        };
      })
      .filter(
        (item) =>
          item.key &&
          Number.isFinite(item.variantId) &&
          Number.isFinite(item.sellingPlanId) &&
          Number.isFinite(item.quantity),
      );

    logger.info("CartDrawerItems", "parseSubscribableItems parsed", { items });

    return items;
  }

  async getExistingSubscriptionSellingPlanName() {
    try {
      const cart = await fetch(`${theme.routes.cart_url}.js`, {
        headers: { Accept: "application/json" },
        cache: "no-store",
      }).then((response) => response.json());

      const existingSubscriptionItem = cart.items.find(
        (item) => item.selling_plan_allocation?.selling_plan?.name,
      );

      return (
        existingSubscriptionItem?.selling_plan_allocation?.selling_plan?.name ||
        null
      );
    } catch (error) {
      logger.warn(
        "CartDrawerItems",
        "Impossible de récupérer la fréquence existante",
        error,
      );
      return null;
    }
  }

  async convertSubscribableItems(button) {
    logger.info("CartDrawerItems", "convertSubscribableItems start", {
      disabled: button?.disabled,
      rawData: button?.dataset?.subscribableItems,
      isConvertingSubscription: this.isConvertingSubscription,
    });

    if (button.disabled || this.isConvertingSubscription) {
      logger.warn("CartDrawerItems", "convertSubscribableItems aborted", {
        buttonDisabled: button?.disabled,
        isConvertingSubscription: this.isConvertingSubscription,
      });
      return;
    }

    const items = this.parseSubscribableItems(button.dataset.subscribableItems);

    logger.info("CartDrawerItems", "convertSubscribableItems parsed items", {
      count: items.length,
      items,
    });

    if (!items.length) {
      logger.warn("CartDrawerItems", "Aucun item transformable trouvé");
      return;
    }

    const existingSellingPlanName =
      await this.getExistingSubscriptionSellingPlanName();

    const itemsToAdd = items.map((item) => {
      const matchingPlan = item.sellingPlans?.find(
        (plan) => plan.name === existingSellingPlanName,
      );

      return {
        ...item,
        sellingPlanId: matchingPlan?.id || item.sellingPlanId,
      };
    });

    logger.info("CartDrawerItems", "subscription selling plan resolved", {
      existingSellingPlanName,
      itemsToAdd,
    });

    const sections = this.getSectionsToRender().map(
      (section) => section.section,
    );
    let hasSucceeded = false;

    this.isConvertingSubscription = true;

    try {
      this.markSubscribableItemsAsLoading(items);
      document.documentElement.classList.add("cart-is-updating");

      logger.info("CartDrawerItems", "STEP 1 - add subscription items", {
        items: itemsToAdd,
        sections,
      });

      const addResponse = await fetch(`${theme.routes.cart_add_url}`, {
        ...fetchConfig(),
        body: JSON.stringify({
          items: itemsToAdd.map((item) => ({
            id: item.variantId,
            quantity: item.quantity,
            selling_plan: item.sellingPlanId,
          })),
          sections,
          sections_url: window.location.pathname,
        }),
      });

      if (!addResponse.ok) {
        throw new Error(`cart_add failed with status ${addResponse.status}`);
      }

      const addState = await addResponse.json();

      logger.info("CartDrawerItems", "STEP 1 - addState", {
        item_count: addState?.item_count,
        sectionKeys: Object.keys(addState?.sections || {}),
      });

      if (addState.errors || addState.status) {
        throw new Error(
          addState.errors || addState.description || "Erreur add subscription",
        );
      }

      logger.info("CartDrawerItems", "STEP 2 - remove oneshot items", {
        count: items.length,
      });

      let lastState = addState;

      for (const item of items) {
        const removeResponse = await fetch(`${theme.routes.cart_change_url}`, {
          ...fetchConfig(),
          body: JSON.stringify({
            id: item.key,
            quantity: 0,
            sections,
            sections_url: window.location.pathname,
          }),
        });

        if (!removeResponse.ok) {
          throw new Error(
            `cart_change failed with status ${removeResponse.status} for key ${item.key}`,
          );
        }

        const removeState = await removeResponse.json();

        logger.info("CartDrawerItems", "STEP 2 - removeState", {
          key: item.key,
          item_count: removeState?.item_count,
          sectionKeys: Object.keys(removeState?.sections || {}),
        });

        if (removeState.errors || removeState.status) {
          throw new Error(
            removeState.errors ||
              removeState.description ||
              `Erreur remove ${item.key}`,
          );
        }

        lastState = removeState;
      }

      logger.info("CartDrawerItems", "STEP 3 - sync fake item");

      const finalState = await syncCartFakeItem({
        baseState: lastState,
        sections,
        sectionsUrl: window.location.pathname,
      });

      logger.info("CartDrawerItems", "STEP 4 - renderDrawerState", {
        item_count: finalState?.item_count,
        sectionKeys: Object.keys(finalState?.sections || {}),
      });

      this.renderDrawerState(finalState);
      hasSucceeded = true;

      await logRealCart("after convertSubscribableItems");
    } catch (error) {
      logger.error("CartDrawerItems", "subscription conversion failed", {
        error,
        items,
        itemsToAdd,
      });

      const errors = document.getElementById("CartDrawer-CartErrors");
      if (errors) {
        errors.textContent = window.cartStrings.error;
      }
    } finally {
      document.documentElement.classList.remove("cart-is-updating");
      this.isConvertingSubscription = false;

      if (!hasSucceeded) {
        this.unmarkSubscribableItemsAsLoading();
      }
    }
  }

  markSubscribableItemsAsLoading(items) {
    logger.info("CartDrawerItems", "markSubscribableItemsAsLoading", { items });

    this.classList.add("pointer-events-none");

    const subscribeCta = this.querySelector("#CartDrawer-SubscribeCta");
    logger.info("CartDrawerItems", "hide subscribe cta", {
      found: !!subscribeCta,
    });
    if (subscribeCta) {
      subscribeCta.setAttribute("hidden", "hidden");
    }

    items.forEach((item) => {
      const lineItem = this.querySelector(
        `[data-key="${CSS.escape(item.key)}"]`,
      );

      logger.info("CartDrawerItems", "mark item lookup", {
        key: item.key,
        found: !!lineItem,
      });

      if (!lineItem) return;

      lineItem.classList.add("pointer-events-none");

      const overlay = lineItem.querySelector(
        ".cart-item__subscription-overlay",
      );
      if (!overlay) return;

      overlay.classList.remove("hidden");
      overlay.classList.add("flex");
      overlay.setAttribute("aria-hidden", "false");
    });
  }

  unmarkSubscribableItemsAsLoading() {
    logger.info("CartDrawerItems", "unmarkSubscribableItemsAsLoading");

    this.classList.remove("pointer-events-none");

    const subscribeCta = this.querySelector("#CartDrawer-SubscribeCta");
    logger.info("CartDrawerItems", "show subscribe cta", {
      found: !!subscribeCta,
    });
    if (subscribeCta) {
      subscribeCta.removeAttribute("hidden");
    }

    this.querySelectorAll(".cart-item").forEach((lineItem) => {
      lineItem.classList.remove("pointer-events-none");

      const overlay = lineItem.querySelector(
        ".cart-item__subscription-overlay",
      );
      if (!overlay) return;

      overlay.classList.add("hidden");
      overlay.classList.remove("flex");
      overlay.setAttribute("aria-hidden", "true");
    });
  }

  syncLandingProductForms(parsedState) {
    document.querySelectorAll("product-form").forEach((productForm) => {
      const button = productForm.querySelector("[data-hide-quantity]");
      if (!button) return;

      const productId = Number(button.dataset.productId);
      if (!productId) return;

      const isStillInCart = parsedState.items?.some(
        (item) => Number(item.product_id) === productId,
      );

      if (isStillInCart) return;

      button.disabled = false;
      button.removeAttribute("aria-disabled");
      button.removeAttribute("data-already-in-cart");

      productForm
        .querySelector("[data-in-cart-label]")
        ?.classList.add("hidden");
      productForm.updateButtonText?.();
    });
  }
}

if (!customElements.get("cart-drawer-items")) {
  customElements.define("cart-drawer-items", CartDrawerItems);
}
